<template>
    <div
        class="main-menu menu-fixed menu-accordion menu-shadow"
        :class="[
            {
                expanded:
                    !isVerticalMenuCollapsed ||
                    (isVerticalMenuCollapsed && isMouseHovered),
            },
            skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
        ]"
        @mouseenter="updateMouseHovered(true)"
        @mouseleave="updateMouseHovered(false)"
    >
        <!-- main menu header-->
        <div class="navbar-header expanded">
            <slot
                name="header"
                :toggle-vertical-menu-active="toggleVerticalMenuActive"
                :toggle-collapsed="toggleCollapsed"
                :collapse-toggler-icon="collapseTogglerIcon"
            >
                <ul class="nav navbar-nav flex-row">
                    <!-- Logo & Text -->
                    <li class="nav-item mr-auto">
                        <b-link class="navbar-brand logo-mt" to="/">
                            <span>
                                <b-img
                                    :src="
                                        isVerticalMenuCollapsed &&
                                        !isMouseHovered
                                            ? appCollapseLogoImage
                                            : appLogoImage
                                    "
                                    alt="logo"
                                    :width="
                                        isVerticalMenuCollapsed &&
                                        !isMouseHovered
                                            ? '45'
                                            : '150'
                                    "
                                />
                            </span>
                        </b-link>
                    </li>

                    <!-- Toggler Button -->
                    <li class="nav-item nav-toggle">
                        <b-link class="nav-link modern-nav-toggle">
                            <feather-icon
                                icon="XIcon"
                                size="20"
                                class="d-block d-xl-none"
                                @click="toggleVerticalMenuActive"
                            />
                            <feather-icon
                                :icon="collapseTogglerIconFeather"
                                size="20"
                                class="d-none d-xl-block collapse-toggle-icon"
                                @click="toggleCollapsed"
                            />
                        </b-link>
                    </li>
                </ul>
            </slot>
        </div>
        <!-- / main menu header-->

        <!-- Shadow -->
        <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

        <!-- main menu content-->
        <vue-perfect-scrollbar
            ref="scroll"
            :settings="perfectScrollbarSettings"
            :watch-options="true"
            class="main-menu-content scroll-area"
            tagname="ul"
            @ps-scroll-y="
                (evt) => {
                    shallShadowBottom = evt.srcElement.scrollTop > 0
                }
            "
        >
            <vertical-nav-menu-items
                :items="navMenuItems"
                class="navigation navigation-main"
            />
        </vue-perfect-scrollbar>
        <!-- /main menu content-->
    </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { BLink, BImg } from 'bootstrap-vue';
import { provide, computed, ref } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';
import { $themeConfig } from '@themeConfig';
import { mapGetters } from 'vuex';
import store from '@/store';
import userNavMenuItems from '@/navigation/vertical/index';
import adminNavMenuItems from '@/navigation/vertical/admin';
import beforeIncorporatedNavMenuItems from '@/navigation/vertical/beforeIncorporated';
import adminAsUserNavMenuItems from '@/navigation/vertical/adminAsUser';
import { isAdmin } from '@/helpers';
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue';
import useVerticalNavMenu from './useVerticalNavMenu';
import { COMPANY } from '@/constants/config';


export default {
    components: {
        VuePerfectScrollbar,
        VerticalNavMenuItems,
        BLink,
        BImg,
    },
    props: {
        isVerticalMenuActive: {
            type: Boolean,
            required: true,
        },
        toggleVerticalMenuActive: {
            type: Function,
            required: true,
        },
    },

    computed: {
        ...mapGetters({
            actAs: 'auth/actAs',
        }),
    },

    setup(props) {
        const {
            isMouseHovered,
            isVerticalMenuCollapsed,
            collapseTogglerIcon,
            toggleCollapsed,
            updateMouseHovered,
        } = useVerticalNavMenu(props);

        const { skin } = useAppConfig();

        // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
        const shallShadowBottom = ref(false);

        provide('isMouseHovered', isMouseHovered);

        const perfectScrollbarSettings = {
            maxScrollbarLength: 60,
            wheelPropagation: false,
        };

        const collapseTogglerIconFeather = computed(() =>
            collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'
        );

        // decider of which menu should be shown
        const getMenus = actAs => {
            const user = store.getters['auth/user'];
            const activeCompanyStatus = store.getters['company/getActiveCompanyStatus'];
            
            // if company is incorporated then accordingly
            if (activeCompanyStatus?.toLowerCase() === COMPANY.STATUS_INCORPORATED.toLowerCase()) {
                if (user && isAdmin(user) && actAs === 'admin') {
                    return adminNavMenuItems;
                }

                if (user && isAdmin(user) && actAs === "user") {
                    return adminAsUserNavMenuItems;
                }

                return userNavMenuItems;
            } 

            // company not incorporated, but user is admin and act as like admin then still show admin menu
            if (user && isAdmin(user) && actAs === 'admin') {
                return adminNavMenuItems;
            }

            // company not incorporated, but user is admin and act as like user then show admin as user menu
            if (user && isAdmin(user) && actAs === 'user') {
                return adminAsUserNavMenuItems;
            }
            
            // company not incorporated, and user is not admin then show before incorporated menu
            return beforeIncorporatedNavMenuItems;
        };

        // App Name
        const { appName, appLogoImage, appCollapseLogoImage } = $themeConfig.app;

        if(! window.localStorage.getItem('actAs')) {
            window.localStorage.actAs = "admin";
        }

        const actAs = window.localStorage.getItem('actAs');
        const navMenuItems = getMenus(actAs);

        return {
            navMenuItems,
            getMenus,
            perfectScrollbarSettings,
            isVerticalMenuCollapsed,
            collapseTogglerIcon,
            toggleCollapsed,
            isMouseHovered,
            updateMouseHovered,
            collapseTogglerIconFeather,

            // Shadow Bottom
            shallShadowBottom,

            // Skin
            skin,

            // App Name
            appName,
            appLogoImage,
            appCollapseLogoImage,
        };
    },

    watch: {
        $route(route) {
            if(route.path.split('/').includes('register'))
            {
                this.$refs.scroll.$el.scrollTop = 140;
                return;
            }
            
            if(! route.path.split('/').includes('company'))
                this.$refs.scroll.$el.scrollTop = 0;
        },

        actAs(actAs) {
            this.navMenuItems = this.getMenus(actAs);

            this.$router.replace({ name: 'company.dashboard' });
        }
    },
};
</script>

<style lang="scss">
.main-menu .navbar-header {
  z-index: 9999;
}
@import '~@core/scss/base/core/menu/menu-types/vertical-menu.scss';
</style>
